import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export const ORDER_SERVICE_STATUS_OPTIONS = [
  { value: 'AGUARDANDO', color: 'primary' },
  { value: 'AGENDADO', color: 'info' },
  { value: 'EM ANDAMENTO', color: 'warning' },
  { value: 'FINALIZADO', color: 'success' },
  { value: 'CANCELADO', color: 'danger' },
]

export const getOrderServiceStatusColor = (status?: string | null) =>
  ORDER_SERVICE_STATUS_OPTIONS.find((option) => option.value === status)?.color || 'secondary'

type Props = {
  id: string
  status?: string | null
  canChange: boolean
  loading?: boolean
  fsBadge?: string
  onChange: (status: string) => void
}

const MENU_GAP = 4
const MENU_MARGIN = 8

/**
 * Badge de status da O.S que abre um menu para trocar o status.
 * O menu é renderizado no body com posição fixa para não ser cortado por containers com overflow.
 */
export function OrderServiceStatusDropdown({ id, status, canChange, loading, fsBadge = '7', onChange }: Props) {
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const statusColor = getOrderServiceStatusColor(status)

  const closeMenu = () => setMenuPos(null)

  const toggleMenu = () => {
    if (menuPos) {
      closeMenu()
      return
    }
    const rect = toggleRef.current?.getBoundingClientRect()
    if (!rect) return
    setMenuPos({ top: rect.bottom + MENU_GAP, left: rect.left })
  }

  // Ajusta a posição depois de medir o menu: não sai pela direita e abre para cima se faltar espaço embaixo.
  useLayoutEffect(() => {
    if (!menuPos || !menuRef.current || !toggleRef.current) return
    const toggleRect = toggleRef.current.getBoundingClientRect()
    const menuRect = menuRef.current.getBoundingClientRect()
    let top = toggleRect.bottom + MENU_GAP
    let left = toggleRect.left
    if (top + menuRect.height > window.innerHeight - MENU_MARGIN) {
      top = Math.max(MENU_MARGIN, toggleRect.top - menuRect.height - MENU_GAP)
    }
    if (left + menuRect.width > window.innerWidth - MENU_MARGIN) {
      left = Math.max(MENU_MARGIN, window.innerWidth - menuRect.width - MENU_MARGIN)
    }
    if (top !== menuPos.top || left !== menuPos.left) {
      setMenuPos({ top, left })
    }
  }, [menuPos])

  useEffect(() => {
    if (!menuPos) return
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || toggleRef.current?.contains(target)) return
      closeMenu()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }
    const handleScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return
      closeMenu()
    }
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', closeMenu)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', closeMenu)
    }
  }, [menuPos])

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      role='presentation'
      style={{ display: 'inline-block', maxWidth: '100%' }}
    >
      <button
        ref={toggleRef}
        type='button'
        id={id}
        className='btn btn-light border-0'
        style={{ padding: '0rem', maxWidth: '100%' }}
        disabled={!canChange || loading}
        title={canChange ? 'Alterar status da O.S.' : undefined}
        aria-haspopup='menu'
        aria-expanded={!!menuPos}
        onClick={toggleMenu}
      >
        <span className={`badge badge-light-${statusColor} fs-${fsBadge} fw-semibold`}>
          {status ?? 'Não identificado'}
          {canChange && (
            loading
              ? <span className='spinner-border spinner-border-sm ms-2' style={{ width: '0.7rem', height: '0.7rem' }} />
              : <i className={`bi bi-chevron-down ms-1 fs-${fsBadge} text-${statusColor}`} />
          )}
        </span>
      </button>

      {menuPos && createPortal(
        <div
          ref={menuRef}
          role='menu'
          aria-labelledby={id}
          className='dropdown-menu show p-2 w-175px shadow'
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, margin: 0, zIndex: 1060 }}
        >
          {ORDER_SERVICE_STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type='button'
              role='menuitem'
              className={`btn btn-sm btn-color-muted btn-active btn-active-${option.color} px-5 mb-1 w-100 ${
                status === option.value ? 'active' : ''
              }`}
              onClick={() => {
                closeMenu()
                if (status !== option.value) onChange(option.value)
              }}
            >
              {option.value}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}
